import React from "react"
import {get, isEmpty, isNil} from 'lodash'
import FinancialString from "containers/financialString"
import DateRangeHelper from './dateRange'
import {JobBoardStatus, ProductionStatus} from './production'
import {permissions} from './constants'
import {quoteChangeActions} from 'actions/types'
import {getAddress, getContactName} from 'helpers/contacts'
import {getNumberFromLetterSequence, getSearchTerm} from 'helpers/string'
import {getPartPackFor} from 'helpers/packs'
import {DeliveryStatus} from 'helpers/deliveries'
import {getPartSaleRate} from 'helpers/parts'
import {isPrintTemplatePublished} from 'helpers/printTemplates'
import {PrintTemplateType} from 'helpers/printTemplates/enum'
import {updateParamsForOrderType} from 'helpers/opportunities'
import {
  explodeSubItems,
  getPricingFromAssemblyContents,
  orderItemIsBuiltType,
  OrderItemPricingMode,
  PriceMode,
  round,
} from './orders'
import FinancialHelper, {
  convertToCurrency,
  getDiscountWithReferralValue,
  getFloat,
  getMargin,
  getMarkup,
  getNetPrice,
  getNumberString,
  getPercentageString,
  getPriceFromMargin,
  getPriceFromMarkup,
  getProfit,
  getReferralValue,
  getSupplierDiscount,
  getSupplierDiscountFromCostInHomeCurrency,
  getTotalValue,
  getUnitCostFromMsrp,
  getUnitPrice,
} from './financial'

export const QuoteStatus = {
  allQuotes: -1,
  activeQuotes: -2,
  recentlyOpened: -3,
  unsent: -4,
  unconfirmed: -5,
  requiredSoon: -6,
  expiring: -7,
  expired: -8,
  notAccepted: -9,
  inProduction: -10,
  inDelivery: -11,
  draft: 0,
  quoted: 1,
  confirmed: 2,
  partDelivered: 9,
  delivered: 3,
  invoiced: 4,
  credited: 5,
  paid: 6,
  closed: 7,
  rejected: 8,
}

export const NotInvoicedStatusOptions = [
  {value: QuoteStatus.confirmed, label: 'Confirmed'},
  {value: QuoteStatus.partDelivered, label: 'Part Delivered'},
  {value: QuoteStatus.delivered, label: 'Delivered'},
]

export const defaultNotInvoicedStatuses = [QuoteStatus.partDelivered, QuoteStatus.delivered]

export const allowedNotInvoicedStatuses = [QuoteStatus.confirmed, QuoteStatus.partDelivered, QuoteStatus.delivered]

export const NotInvoicedDateTypes = [
  {value: 'confirmed', label: 'Confirmed On'},
  {value: 'delivered', label: 'Delivered On'},
]

export const statusesToExcludeFromProduction = [
  QuoteStatus.draft,
  QuoteStatus.quoted,
  QuoteStatus.closed,
  QuoteStatus.rejected,
]

export const isDraftQuote = (status) => {
  return status === QuoteStatus.draft
}

export const isConfirmedQuote = (status) => {
  return status === QuoteStatus.confirmed
}

export const orderHasBeenQuoted = (order) => {
  return isNil(order)
    ? false
    : [
      QuoteStatus.quoted,
      QuoteStatus.rejected,
      QuoteStatus.confirmed,
      QuoteStatus.partDelivered,
      QuoteStatus.delivered,
      QuoteStatus.invoiced,
      QuoteStatus.credited,
      QuoteStatus.paid,
    ].includes(order.status.value)
}

// Whether to show the "Send Automatic Reminders" toggle on a quote status change:
// only when moving draft -> quoted, the account has automatic quote reminders
// enabled, and at least one reminder is configured.
export const shouldShowSendRemindersToggle = (isMovingToQuotedFromDraft, quotingRemindersEnabled, hasQuotingReminders) => {
  return !!(isMovingToQuotedFromDraft && quotingRemindersEnabled && hasQuotingReminders)
}

// The value sent to the backend for whether automatic reminders should be sent for
// this quote: only true when the account has them enabled, at least one reminder is
// configured, and the user left the toggle on. Resolves to a definite boolean so the
// flag can always be sent (false when not relevant) rather than conditionally omitted.
export const shouldSendAutomaticReminders = (quotingRemindersEnabled, hasQuotingReminders, sendAutomaticReminders) => {
  return !!(quotingRemindersEnabled && hasQuotingReminders && sendAutomaticReminders)
}

export const isConfirmedJob = (order) => {
  return isNil(order)
    ? false
    : [
      QuoteStatus.confirmed,
      QuoteStatus.partDelivered,
      QuoteStatus.delivered,
      QuoteStatus.invoiced,
      QuoteStatus.credited,
      QuoteStatus.paid,
    ].includes(order.status.value)
}

export const JobStatuses = [
  {id: 0, label: 'Draft Enquiries'},
  {id: 1, label: 'Outstanding Quotes'},
  {id: 2, label: 'Order Book'},
  {id: 5, label: 'Job Schedule'},
  {id: 3, label: 'Awaiting Invoicing'},
  {id: 4, label: 'Unpaid Invoices'},
  {id: 6, label: 'Target Completion'},
]

export const TextTemplateTypes = {
  terms: 1,
  header: 2,
  footer: 3,
  productionNotes: 4,
  purchasingTerms: 5,
}

export const ReferralType = {
  none: 0,
  percentage: 1,
  value: 2,
}

export const OrderReasonType = {
  order: 0,
  reject: 1,
}

export const getReferralTypeName = (referralType) => {
  switch (referralType) {
    case ReferralType.none:
      return 'None'
    case ReferralType.percentage:
      return 'Percentage'
    case ReferralType.value:
      return 'Value'
    default:
      break
  }
}

export const getFormattedReferralValue = (homeCurrency, referralValue, referralType) => {
  const currencyId = get(homeCurrency, 'id', null)
  switch (referralType) {
    case ReferralType.none:
      return
    case ReferralType.percentage:
      return `${referralValue}%`
    case ReferralType.value:
      return <FinancialString value={referralValue} currencyId={currencyId}/>
    default:
      break
  }
}

export const CoverReferralFee = {
  notSet: 0,
  set: 1,
}

export const getCoverReferralFeeName = (coverReferralFee) => {
  switch (coverReferralFee) {
    case CoverReferralFee.notSet:
      return 'NOT_SET'
    case CoverReferralFee.set:
      return 'SET'
    default:
      break
  }
}

export const AllQuoteStatuses = [
  QuoteStatus.draft,
  QuoteStatus.quoted,
  QuoteStatus.confirmed,
  QuoteStatus.delivered,
  QuoteStatus.invoiced,
  QuoteStatus.credited,
  QuoteStatus.paid,
  QuoteStatus.closed,
  QuoteStatus.rejected,
]

export const findQuoteStatus = (id) => {
  switch (id) {
    case 0:
      return QuoteStatus.draft
    case 1:
      return QuoteStatus.quoted
    case 2:
      return QuoteStatus.confirmed
    case 3:
      return QuoteStatus.delivered
    case 4:
      return QuoteStatus.invoiced
    case 5:
      return QuoteStatus.credited
    case 6:
      return QuoteStatus.paid
    case 7:
      return QuoteStatus.closed
    case 8:
      return QuoteStatus.rejected
    case 9:
      return QuoteStatus.partDelivered
  }
}

export const getQuoteTypes = () => {
  return [
    {id: -1, title: 'All Quotes'},
    {id: -2, title: 'All Active Quotes'},
    {id: -3, title: 'Recently Opened'},
    {id: 0, title: 'Draft'},
    {id: 1, title: 'Quoted'},
    {id: 2, title: 'Confirmed'},
    {id: 9, title: 'Part Delivered'},
    {id: 3, title: 'Delivered'},
    {id: 4, title: 'Invoiced'},
    {id: 5, title: 'Credited'},
    {id: 6, title: 'Paid'},
    {id: 7, title: 'Closed'},
    {id: 8, title: 'Rejected'},
    {id: -4, title: 'Unsent'},
    {id: -5, title: 'Unconfirmed'},
    {id: -6, title: 'Required Soon'},
    {id: -7, title: 'Expiring'},
    {id: -8, title: 'Expired'},
    {id: -9, title: 'Not Accepted'},
    {id: -10, title: 'In Production'},
    {id: -11, title: 'In Delivery'},
  ]
}

export const QuoteDateTypes = [
  {value: 'created', label: 'Created On'},
  {value: 'modified', label: 'Modified On'},
  {value: 'quoted', label: 'Quoted On'},
  {value: 'expected', label: 'Expected On'},
  {value: 'acceptance', label: 'Accepted On'},
  {value: 'rejected', label: 'Rejected On'},
  {value: 'confirmed', label: 'Confirmed On'},
  {value: 'delivered', label: 'Delivered On'},
  {value: 'paid', label: 'Paid On'},
  {value: 'invoiced', label: 'Invoiced On'},
  {value: 'target', label: 'Target Completion'},
  {value: 'required', label: 'Required On'},
]

export const ApprovalDateTypes = [
  {value: 'created', label: 'Created On', disabled: false},
  {value: 'modified', label: 'Modified On', disabled: false},
  {value: 'approved', label: 'Approved On', disabled: false},
  {value: 'requestedChanges', label: 'Requested Changes On', disabled: false},
  {value: 'rejected', label: 'Rejected On', disabled: false},
]

export const DeliveryQuoteDateTypes = [
  {value: 'created', label: 'Created On'},
  {value: 'confirmed', label: 'Confirmed On'},
  {value: 'paid', label: 'Paid On'},
  {value: 'invoiced', label: 'Invoiced On'},
  {value: 'target', label: 'Target Completion'},
  {value: 'required', label: 'Required On'},
]

export const JobStatusDateTypes = [
  {value: 'created', label: 'Created On'},
  {value: 'modified', label: 'Modified On'},
  {value: 'target', label: 'Target Completion'},
  {value: 'required', label: 'Required On'},
]

export const SalesAnalysisQuoteDateTypes = [
  {value: 'created', label: 'Created On'},
  {value: 'modified', label: 'Modified On'},
  {value: 'quoted', label: 'Quoted On'},
  {value: 'expected', label: 'Expected On'},
  {value: 'acceptance', label: 'Accepted On'},
  {value: 'rejected', label: 'Rejected On'},
  {value: 'confirmed', label: 'Confirmed On'},
  {value: 'delivered', label: 'Delivered On'},
  {value: 'paid', label: 'Paid On'},
  {value: 'invoiced', label: 'Invoiced On'},
  {value: 'target', label: 'Target Completion'},
  {value: 'required', label: 'Required On'},
]

export const DeliveryDateTypes = [
  {value: 'started', label: 'Started On'},
  {value: 'finished', label: 'Finished On'},
  {value: 'scheduled', label: 'Scheduled On'},
  {value: 'created', label: 'Created On'},
  {value: 'modified', label: 'Modified On'},
]

export const DeliveryPersonTypes = [
  {value: 'estimator', label: 'Estimated By'},
  {value: 'salesman', label: 'Salesperson'},
  {value: 'accountManager', label: 'Account Manager'},
]

export const PersonTypes = [
  {value: 'personCreator', label: 'Created By'},
  {value: 'personEstimator', label: 'Estimated By'},
  {value: 'personSalesman', label: 'Salesperson'},
]

export const getQuotePersonParams = (params, contacts, selectedRoles) => {
  if (isEmpty(contacts)) {
    return params
  }
  params['personId'] = contacts

  const matchEveryRole = isEmpty(selectedRoles)
  PersonTypes.forEach(({value: role}) => {
    params[role] = matchEveryRole || selectedRoles.includes(role)
  })

  return params
}

export const ApprovalPersonTypes = [
  {value: 'personEstimator', label: 'Estimated By', disabled: false},
  {value: 'personOwner', label: 'Owner', disabled: false},
  {value: 'personSalesman', label: 'Salesperson', disabled: false},
]

export const getParamsForType = (
  {
    timeKey,
    dateType = null,
    type,
    personId = null,
    companyId = null,
    excludeHomeCompany = false,
    limit = null,
    invoiceFinancialYearStart,
    proSync = false,
  }) => {

  let params = {}
  const {startDate, endDate} = DateRangeHelper.getDateRangeForKey(timeKey, invoiceFinancialYearStart)
  const now = new Date().getTime()

  let applyDateFilter = true

  if (type != null && type.length > 0) {
    switch (type[0]) {

      case QuoteStatus.recentlyOpened:
        // Special case should be handled outside this method
        params = {recent: true}
        applyDateFilter = false
        break

      case QuoteStatus.requiredSoon:
        params = {
          status: QuoteStatus.confirmed,
          requiredStart: now,
          requiredEnd: now + (7 * 86400000),
        }
        applyDateFilter = false
        break

      case QuoteStatus.expiring:
        params = {
          status: QuoteStatus.quoted,
          expiryStart: now,
          expiryEnd: now + (7 * 86400000),
        }
        applyDateFilter = false
        break

      default:
        switch (type[0]) {

          case QuoteStatus.unsent:
            params = {
              status: QuoteStatus.draft,
              acceptanceRequested: false,
            }
            dateType = dateType || 'modified'
            break

          case QuoteStatus.unconfirmed:
            params = {
              status: QuoteStatus.quoted,
              acceptanceRequested: false,
            }
            dateType = dateType || 'quoted'
            break

          case QuoteStatus.expired:
            params = {
              status: [
                QuoteStatus.draft,
                QuoteStatus.quoted,
              ],
              expiryEnd: now,
            }
            break

          case QuoteStatus.notAccepted:
            params = {
              status: QuoteStatus.quoted,
              accepted: false,
              acceptanceRequested: true,
            }
            dateType = dateType || 'acceptanceSent'
            break

          case QuoteStatus.inProduction:
            params = proSync
              ? {
                status: QuoteStatus.confirmed,
                productionStatus: [
                  ProductionStatus.scheduled,
                  ProductionStatus.wip,
                  ProductionStatus.onHold,
                ],
              }
              : {
                status: QuoteStatus.confirmed,
                jobBoardStatus: [
                  JobBoardStatus.pending,
                  JobBoardStatus.wip,
                  JobBoardStatus.onHold,
                ],
              }
            dateType = dateType || 'confirmed'
            break

          case QuoteStatus.inDelivery:
            params = proSync
              ?
              {
                status: QuoteStatus.confirmed,
                productionStatus: ProductionStatus.complete,
              } : {
                status: QuoteStatus.confirmed,
                deliveryStatus: [
                  DeliveryStatus.none,
                  DeliveryStatus.partDelivered,
                ],
              }
            dateType = dateType || 'confirmed'
            break

          case QuoteStatus.allQuotes:
            // All quotes
            break

          case QuoteStatus.activeQuotes:
            params = {
              status: [
                QuoteStatus.draft,
                QuoteStatus.quoted,
                QuoteStatus.confirmed,
                QuoteStatus.delivered,
                QuoteStatus.invoiced,
                QuoteStatus.credited,
              ],
            }
            break

          default:  // any other multi-selectable status
            params = {
              status: type,
            }
            break
        }
    }
  }

  if (applyDateFilter) {
    dateType = dateType || 'modified'
    params[dateType + 'Start'] = startDate
    params[dateType + 'End'] = endDate
  }

  if (limit !== null) params['limit'] = limit
  if (personId !== null) params['personId'] = personId
  if (companyId !== null) params['companyId'] = companyId
  if (excludeHomeCompany !== null) params['excludeHomeCompany'] = excludeHomeCompany

  return params
}

export const getStatusForSelectedQuoteList = (selectedQuotesList) => {
  if (selectedQuotesList === 'Total Quotes') {
    return null
  } else if (selectedQuotesList === 'Successful Quotes') {
    return [2, 3, 4, 5, 6, 7]
  } else if (selectedQuotesList === 'Outstanding Quotes') {
    return null // TODO needs clarification
  } else if (selectedQuotesList === 'Unsuccessful Quotes') {
    return 8
  }
}

export const getQuoteStatus = (status) => {
  let quoteStatus
  switch (status) {
    case QuoteStatus.draft:
      quoteStatus = 'Draft'
      break
    case QuoteStatus.quoted:
      quoteStatus = 'Quoted'
      break
    case QuoteStatus.confirmed:
      quoteStatus = 'Confirmed'
      break
    case QuoteStatus.partDelivered:
      quoteStatus = 'Part Delivered'
      break
    case QuoteStatus.delivered:
      quoteStatus = 'Delivered'
      break
    case QuoteStatus.invoiced:
      quoteStatus = 'Invoiced'
      break
    case QuoteStatus.credited:
      quoteStatus = 'Credited'
      break
    case QuoteStatus.paid:
      quoteStatus = 'Paid'
      break
    case QuoteStatus.closed:
      quoteStatus = 'Closed'
      break
    case QuoteStatus.rejected:
      quoteStatus = 'Rejected'
      break
    default:
      quoteStatus = 'Not Specified'
      break
  }
  return quoteStatus
}

export const getColorForProductionStatus = (muiTheme, status) => {
  if (isNil(status)) return ''
  switch (status) {
    case -1:
      return muiTheme.palette.productionStatus.unscheduled
    case 0:
      return muiTheme.palette.productionStatus.scheduled
    case 1:
      return muiTheme.palette.productionStatus.wip
    case 2:
      return muiTheme.palette.productionStatus.onHold
    case 3:
      return muiTheme.palette.productionStatus.complete
    default:
      return ''
  }
}

export const getColorForQuoteStatus = (muiTheme, status) => {
  if (isNil(status)) return ''
  switch (status) {
    case QuoteStatus.draft:
      return muiTheme.palette.quoteStatus.draft
    case QuoteStatus.quoted:
      return muiTheme.palette.quoteStatus.quoted
    case QuoteStatus.confirmed:
      return muiTheme.palette.quoteStatus.confirmed
    case QuoteStatus.delivered:
      return muiTheme.palette.quoteStatus.delivered
    case QuoteStatus.partDelivered:
      return muiTheme.palette.quoteStatus.partDelivered
    case QuoteStatus.invoiced:
      return muiTheme.palette.quoteStatus.invoiced
    case QuoteStatus.credited:
      return muiTheme.palette.quoteStatus.credited
    case QuoteStatus.paid:
      return muiTheme.palette.quoteStatus.paid
    case QuoteStatus.closed:
      return muiTheme.palette.quoteStatus.closed
    case QuoteStatus.rejected:
      return muiTheme.palette.quoteStatus.rejected
    case QuoteStatus.inDelivery:
      return muiTheme.palette.quoteStatus.inDelivery
    default:
      return ''
  }
}

export const getOpportunityName = (opportunity) => {
  if (opportunity !== null && opportunity !== undefined) {
    const ref = (opportunity.ref !== null && opportunity.ref !== undefined) ? opportunity.ref : ''
    const desc = (opportunity.desc !== null && opportunity.desc !== undefined) ? opportunity.desc : ''
    return ref + ': ' + desc
  } else {
    return ''
  }
}

export const getAccountManagerName = (contact) => {
  const accountManager = get(contact, 'accountmanager', null)
  return isNil(accountManager)
    ? ''
    : getContactName(accountManager)
}

export const getNameFromObjectForKey = (order, key) => {
  if (order !== null && order !== undefined) {
    if (order[key]) {
      return order[key].name
    }
  }
  return ''
}

export const filterDeletedOrderItems = (orderItems) => {
  if (!isNil(orderItems)) {
    return orderItems.filter((orderItem) => {
      return orderItem.deleted !== true
    })
  } else {
    return []
  }
}

// Returns the equivalent % of the line item that needs to be allocated as a referral fee
// This is either specified at line item level, or inferred from the quote level and
// only if 'cover referral' is enabled
export const getOrderItemReferralFee = (orderItem) => {
  if (orderItem.coverReferral) return orderItem.referralValue || orderItem.orderReferralValue
  return 0
}

export const updateOrderItemUnitValuesAreSet = (orderItem, pricing, subItems = null) => {
  const useAssemblyPricing = orderItemIsBuiltType(orderItem)
    && !isNil(subItems)
    && orderItem.pricingMode === OrderItemPricingMode.contents
  const assemblyPricing = useAssemblyPricing
    ? getPricingFromAssemblyContents(orderItem, explodeSubItems(subItems), orderItem)
    : null
  const unitPriceOrigin = useAssemblyPricing ? assemblyPricing.unitPrice : get(pricing, 'unitPrice')
  const unitCostOrigin = useAssemblyPricing ? assemblyPricing.unitCost : get(pricing, 'unitCost')

  return {
    ...orderItem,
    unitPriceSet: !isNil(unitPriceOrigin)
      && round(orderItem.unitPrice, 2) !== round(unitPriceOrigin, 2),
    unitCostSet: !isNil(unitCostOrigin)
      && round(orderItem.unitCost, 2) !== round(unitCostOrigin, 2),
  }
}

export const getCalculatedOrderItem = (
  orderItem,
  currencies,
  editedField,
  pricing = null,
  requiredQuantity = null,
  pricingPrecision = null
) => {

  if (orderItem) {
    const currencyRate = getFloat(orderItem.currencyRate)
    const pricingRate = getFloat(
      isNil(orderItem.pricingRate)
        ? getPartSaleRate(orderItem.part, currencies)
        : orderItem.pricingRate
    )

    const quantity = getFloat(
      isNil(requiredQuantity)
        ? orderItem.quantity
        : requiredQuantity
    )
    const discount = getFloat(orderItem.discount)
    const taxRate = getFloat(orderItem.taxRate)
    const msrp = getFloat(
      isNil(orderItem.msrp)
        ? getOrderItemMsrp(orderItem)
        : orderItem.msrp
    )
    const supplierDiscount = getFloat(orderItem.supplierDiscount)
    const referralValue = getFloat(getOrderItemReferralFee(orderItem))

    // Obtain or back-calculate existing margin/markup
    let margin = !isNil(orderItem.margin)
      ? getFloat(orderItem.margin)
      : getMargin(orderItem.unitPrice, orderItem.unitCost, currencyRate)
    let markup = !isNil(orderItem.markup)
      ? getFloat(orderItem.markup)
      : getMarkup(orderItem.unitPrice, orderItem.unitCost, currencyRate)

    // Re-calculate unit cost/price
    let unitCost = getFloat(orderItem.unitCost)
    let unitPrice = getFloat(orderItem.unitPrice)

    // Get pricing from part
    if (pricing) {
      unitPrice = getPrice(pricing.unitPrice, pricingPrecision)
      unitCost = getPrice(pricing.unitCost, pricingPrecision)
      orderItem.unitPriceSet = pricing.unitPriceSet
      orderItem.unitCostSet = pricing.unitCostSet

      if (pricing.metadata) {
        const {description, notes} = pricing.metadata
        if (description) orderItem.desc = description
        if (notes) orderItem.notes = notes
      }
    }

    // Recalculate from control fields
    if (editedField === 'supplierDiscount') {
      unitCost = getUnitCostFromMsrp(msrp, supplierDiscount, pricingRate)
    }
    if (orderItem.useMSRPAsPrice) {
      unitPrice = msrp / pricingRate * currencyRate
    }
    if (editedField === 'margin') {
      const priceFromMargin = getPriceFromMargin(unitCost, margin, currencyRate)
      unitPrice = getPrice(priceFromMargin, pricingPrecision)
    }
    if (editedField === 'unitPrice') {
      unitPrice = getFloat(orderItem.unitPrice)
    }
    if (['unitCost', 'markup', 'supplierDiscount'].includes(editedField)) {
      const priceFromMarkup = getPriceFromMarkup(unitCost, markup, currencyRate)
      unitPrice = getPrice(priceFromMarkup, pricingPrecision)
    }
    orderItem.unitPrice = getFloat(unitPrice)
    orderItem.unitCost = getFloat(unitCost)

    // Calculate totalCost
    const totalCost = getTotalValue(unitCost, quantity)

    orderItem.totalCost = totalCost

    // Calculate totalPrice
    const totalPrice = getTotalValue(unitPrice, quantity)
    orderItem.totalPrice = totalPrice

    // Calculate net price
    const netPrice = getNetPrice(discount, totalPrice, referralValue)
    orderItem.netPrice = netPrice

    // Calculate margin
    if (editedField !== 'margin') margin = getMargin(totalPrice, totalCost, currencyRate)
    orderItem.margin = margin

    // Calculate markup
    if (editedField !== 'markup') markup = getMarkup(totalPrice, totalCost, currencyRate)
    orderItem.markup = markup

    // Calculate net unit price
    orderItem.netUnitPrice = getUnitPrice(netPrice, quantity)

    // Calculate net markup and net margin
    orderItem.netMarkup = getMarkup(netPrice, totalCost, currencyRate)
    orderItem.netMargin = getMargin(netPrice, totalCost, currencyRate)

    // Calculate supplier discount
    if (editedField !== 'supplierDiscount') {
      orderItem.supplierDiscount = getSupplierDiscount(unitCost, msrp, pricingRate)
    }

    // Calculate profit
    orderItem.profit = getProfit(netPrice, totalCost, currencyRate)

    // Calculate tax
    orderItem.tax = totalPrice * taxRate
    orderItem.netTax = netPrice * taxRate

    return orderItem
  }
}

const getPrice = (value, precision) => {
  return !isNil(precision)
    ? getNumberString(value, precision)
    : value
}

export const getCalculatedTotals = (order, items, user) => {
  let totalPrice = 0
  let totalTax = 0
  let subTotalPrice = 0
  let subTotalTax = 0
  let totalCost = 0

  const priceModeId = get(order, 'priceMode.id', null)
  items.forEach(item => {
    if (!item.dormant) {

      const price = FinancialHelper.round(item.netPrice, 2)
      let discountedPrice = price

      if (priceModeId === PriceMode.pc_discount && order.discountPc) {
        discountedPrice =
          FinancialHelper.round(price, 10) - (FinancialHelper.round(price, 10) * order.discountPc / 100)
        discountedPrice = FinancialHelper.round(discountedPrice, 2)
      }

      let tax = FinancialHelper.round(price * item.taxRate, 10)
      let discountedTax = FinancialHelper.round(discountedPrice * item.taxRate, 10)

      if (user.settings.lineItemTaxRounding) {
        tax = FinancialHelper.round(tax, 2)
        discountedTax = FinancialHelper.round(discountedTax, 2)
      }

      totalCost += item.totalCost
      subTotalPrice += price
      subTotalTax += tax
      totalPrice += discountedPrice
      totalTax += discountedTax
    }
  })

  // Apply price modifiers
  switch (priceModeId) {

    case PriceMode.pc_discount: {
      // This is applied per line, to ensure same rounding in Pro.
      break
    }

    /* Not currently supported
      case PriceMode.fixed_discount: {
          // Subtract fixed discount from total
          if (order.discountValue) {
              totalPrice = subTotalPrice - discountValue
              if (totalPrice < 0) totalPrice = 0

              // Factor tax given % difference
              let pc = (subTotalPrice == 0) ? 0 : discountValue / subTotalPrice * 100
              totalTax = subTotalTax - (subTotalTax * pc / 100)
          }
          break
      }
    */

    case PriceMode.fixed_margin: {
      // Apply fixed price and fixed tax based on quote rate
      if (order.margin != null) {
        totalPrice = getPriceFromMargin(totalCost, order.margin, order.currencyRate)
        totalTax = totalPrice * order.taxRate
      }
      break
    }

    case PriceMode.fixed_price: {
      // Apply fixed price and fixed tax based on quote rate
      if (order.fixedPrice != null) {
        totalPrice = order.fixedPrice
        totalTax = totalPrice * order.taxRate
      }
      break
    }

    default:
      break
  }

  return {
    ...order,
    totalCost,
    margin: getMargin(totalPrice, totalCost, order.currencyRate),
    subTotalPrice,
    subTotalTax,
    totalPrice,
    totalTax,
    totalPriceIncTax: totalPrice + totalTax,
    reportedPrice: !isNil(order.grossValue) ? order.grossValue : totalPrice,
  }
}

export const getNominalCode = (entity) => {
  if (!isNil(entity)) {
    return get(entity, 'nominalCode', null) ||
      get(entity, 'nominalCode.code', null)
  } else {
    return null
  }
}

export const getPrintTemplatesOfType = (printTemplates, types) => {
  return printTemplates.filter(printTemplate => types.includes(printTemplate.type))
}

export const getPrintTemplatesForOrderStatus = (printTemplates, orderStatus) => {
  if (isNil(printTemplates) || isNil(orderStatus)) {
    throw new Error('Invalid argument')
  } else {
    switch (orderStatus) {
      case QuoteStatus.draft:
        return getPrintTemplatesOfType(printTemplates, [
          PrintTemplateType.DRAFT,
          PrintTemplateType.QUOTATION,
        ])
      case QuoteStatus.quoted:
        return getPrintTemplatesOfType(printTemplates, [
          PrintTemplateType.QUOTATION,
          PrintTemplateType.CONFIRMATION,
          PrintTemplateType.PROFORMA_INVOICE,
        ])
      case QuoteStatus.confirmed:
        return getPrintTemplatesOfType(printTemplates, [
          PrintTemplateType.QUOTATION,
          PrintTemplateType.CONFIRMATION,
          PrintTemplateType.PROFORMA_INVOICE,
        ])
      case QuoteStatus.partDelivered:
        return getPrintTemplatesOfType(printTemplates, [
          PrintTemplateType.QUOTATION,
          PrintTemplateType.CONFIRMATION,
          PrintTemplateType.PROFORMA_INVOICE,
        ])
      case QuoteStatus.delivered:
        return getPrintTemplatesOfType(printTemplates, [
          PrintTemplateType.QUOTATION,
          PrintTemplateType.CONFIRMATION,
          PrintTemplateType.PROFORMA_INVOICE,
        ])
      case QuoteStatus.invoiced:
        return getPrintTemplatesOfType(printTemplates, [
          PrintTemplateType.QUOTATION,
          PrintTemplateType.CONFIRMATION,
        ])
      case QuoteStatus.credited:
        return getPrintTemplatesOfType(printTemplates, [
          PrintTemplateType.QUOTATION,
          PrintTemplateType.CONFIRMATION,
        ])
      case QuoteStatus.paid:
        return getPrintTemplatesOfType(printTemplates, [
          PrintTemplateType.QUOTATION,
          PrintTemplateType.CONFIRMATION,
        ])
      case QuoteStatus.closed:
        return getPrintTemplatesOfType(printTemplates, [
          PrintTemplateType.DRAFT,
        ])
      case QuoteStatus.rejected:
        return getPrintTemplatesOfType(printTemplates, [
          PrintTemplateType.QUOTATION,
        ])
      default:
        return printTemplates
    }
  }
}

export const getPrintTemplatesForOrder = (printTemplates, order) => {
  if (isNil(printTemplates) || isNil(order)) {
    throw new Error('Invalid argument')
  } else {

    let ret = getPrintTemplatesForOrderStatus(printTemplates, order.status.value)
    if (order.acceptedDate)
      ret = [...ret, ...getPrintTemplatesOfType(printTemplates, [PrintTemplateType.ACCEPTANCE])]

    return ret.filter(isPrintTemplatePublished)
  }
}

export const getTextTemplateOptionsByType = (textTemplates, type) => {
  if (isNil(textTemplates) || isNil(type)) {
    throw new Error('Invalid argument')
  } else {
    return textTemplates
      .filter(t => t.type && t.type.id === type)
      .map(t => ({key: t.id, label: t.name, text: t.text}))
  }
}

export const getValidatedSendAddresses = (sendAddresses) => {
  if (isNil(sendAddresses)) {
    return []
  } else {
    return sendAddresses.filter(sa => sa.usable === true)
  }
}

export const mapSendAddressesToOptions = (sendAddresses) => {
  return sendAddresses.map(sendAddress => {
    return {
      key: sendAddress.address,
      value: sendAddress.address,
      label: sendAddress.address,
      sendAddress,
    }
  })
}

export const getCurrency = (currencyId, currencies) => {
  if (isNil(currencyId) || isNil(currencies)) {
    return ''
  } else {
    return currencies.filter((currency) => currency.id === currencyId)[0]
  }
}

export const supportsAmend = (status) => {
  return status === QuoteStatus.confirmed
    || status === QuoteStatus.partDelivered
    || status === QuoteStatus.delivered
    || status === QuoteStatus.invoiced
}

export const canEditQuote = (order, user) => {
  switch (order.status.value) {
    case QuoteStatus.draft:
    case QuoteStatus.quoted:
    case QuoteStatus.closed:
      return true

    // Post-confirm requires the 'CONFIRM_JOBS' permission
    default:
      return (user.permissions.indexOf(permissions.CONFIRM_JOBS) >= 0)
        || (user.permissions.indexOf(permissions.AMEND_QUOTES) >= 0)
  }
}

// Returns a user-friendly message explaining why you cannot edit the quote
// Returns NULL if you are allowed to edit the quote
export const getEditQuoteReason = (order, user) => {
  switch (order.status.value) {
    case QuoteStatus.draft:
    case QuoteStatus.quoted:
    case QuoteStatus.closed:
      break

    // Post-confirm requires the 'CONFIRM_JOBS' permission
    default: {
      if (user.permissions.indexOf(permissions.CONFIRM_JOBS) < 0) {
        return 'You do not have permission to edit this quote'
      }
      break
    }
  }
  return null
}

export const filterDeletedItems = (items, deletedItems) => {
  return items.filter(i => !deletedItems.includes(i.id))
}

// deletedItems holds sub-item ids as well as line item ids
export const filterDeletedItemsDeep = (items, deletedItems) => {
  return filterDeletedItems(items, deletedItems).map(item => ({
    ...item,
    subItems: item.subItems ? filterDeletedItemsDeep(item.subItems, deletedItems) : item.subItems,
  }))
}

export const isHomeCompany = (homeCompanyId, companyId) => {
  return homeCompanyId === companyId
}

export const getCompanyId = (companyId, homeCompanyId, excludeHomeCompany) => {
  if (isNil(companyId)) return null
  if (excludeHomeCompany && isHomeCompany(homeCompanyId, companyId.value)) {
    return null
  }
  return companyId.value
}

// List of features
export const QuoteFeature = {
  EDIT_DESCRIPTION: 0,
  EDIT_CONTACT: 1,
  EDIT_ESTIMATOR: 2,
  EDIT_SALESPERSON: 3,
  SET_OPPORTUNITY: 4,
  CLEAR_OPPORTUNITY: 5,
  EDIT_CAMPAIGN: 6,
  EDIT_JOB_TYPE: 7,
  ADJUST_TOTAL_PRICE: 8,
  EDIT_ORDER_NUMBER: 9,
  EDIT_GROSS_VALUE: 10,
  EDIT_PROBABILITY: 11,
  EDIT_TAX_CODE: 12,
  EDIT_LETTER_HEADER: 13,
  EDIT_LETTER_FOOTER: 14,
  EDIT_SIGNATURE: 15,
  EDIT_TERMS: 17,
  CHANGE_PRINT_OPTIONS: 18,
  EDIT_ORDER_REASON: 19,
  EDIT_REJECT_REASON: 20,
  EDIT_REJECT_NOTES: 21,
  EDIT_EXPECTED_DATE: 22,
  EDIT_QUOTE_REQUIRED_DATE: 23,
  EDIT_PLANNED_START_DATE: 24,
  EDIT_TARGET_DATE: 25,
  EDIT_REQUIRED_DATE: 26,
  EDIT_EXPIRY_DATE: 27,
  EDIT_ADDRESSES: 28,
  EDIT_NOTES: 29,
  EDIT_USER_FIELDS: 30,
  ADD_LINE_ITEMS: 31,
  EDIT_LINE_ITEMS: 32,
  DELETE_LINE_ITEMS: 33,
  DORMANT_LINE_ITEMS: 34,
  REORDER_LINE_ITEMS: 35,
  CHANGE_QUOTE_STATUS: 36,
  CREATE_NEW_REVISION: 37,
  VIEW_AMENDMENTS: 38,
  EDIT_REFERRALS: 39,
  CLEAR_CONTACT: 40,
}

// List of features enabled at each stage
export const QuoteStatusFeatures = {
  [QuoteStatus.draft]: {
    [QuoteFeature.EDIT_DESCRIPTION]: true,
    [QuoteFeature.EDIT_CONTACT]: true,
    [QuoteFeature.CLEAR_CONTACT]: false,
    [QuoteFeature.EDIT_ESTIMATOR]: true,
    [QuoteFeature.EDIT_SALESPERSON]: true,
    [QuoteFeature.SET_OPPORTUNITY]: true,
    [QuoteFeature.CLEAR_OPPORTUNITY]: true,
    [QuoteFeature.EDIT_CAMPAIGN]: true,
    [QuoteFeature.EDIT_JOB_TYPE]: true,
    [QuoteFeature.ADJUST_TOTAL_PRICE]: true,
    [QuoteFeature.EDIT_ORDER_NUMBER]: true,
    [QuoteFeature.EDIT_GROSS_VALUE]: true,
    [QuoteFeature.EDIT_PROBABILITY]: true,
    [QuoteFeature.EDIT_TAX_CODE]: true,
    [QuoteFeature.EDIT_LETTER_HEADER]: true,
    [QuoteFeature.EDIT_LETTER_FOOTER]: true,
    [QuoteFeature.EDIT_SIGNATURE]: true,
    [QuoteFeature.EDIT_TERMS]: true,
    [QuoteFeature.CHANGE_PRINT_OPTIONS]: true,
    [QuoteFeature.EDIT_ORDER_REASON]: false,
    [QuoteFeature.EDIT_REJECT_REASON]: false,
    [QuoteFeature.EDIT_REJECT_NOTES]: false,
    [QuoteFeature.EDIT_EXPECTED_DATE]: true,
    [QuoteFeature.EDIT_QUOTE_REQUIRED_DATE]: true,
    [QuoteFeature.EDIT_PLANNED_START_DATE]: true,
    [QuoteFeature.EDIT_TARGET_DATE]: true,
    [QuoteFeature.EDIT_REQUIRED_DATE]: true,
    [QuoteFeature.EDIT_EXPIRY_DATE]: false,
    [QuoteFeature.EDIT_ADDRESSES]: true,
    [QuoteFeature.EDIT_NOTES]: true,
    [QuoteFeature.EDIT_USER_FIELDS]: true,
    [QuoteFeature.ADD_LINE_ITEMS]: true,
    [QuoteFeature.EDIT_LINE_ITEMS]: true,
    [QuoteFeature.DELETE_LINE_ITEMS]: true,
    [QuoteFeature.DORMANT_LINE_ITEMS]: false,
    [QuoteFeature.REORDER_LINE_ITEMS]: true,
    [QuoteFeature.CHANGE_QUOTE_STATUS]: true,
    [QuoteFeature.CREATE_NEW_REVISION]: false,
    [QuoteFeature.VIEW_AMENDMENTS]: false,
    [QuoteFeature.EDIT_REFERRALS]: true,
  },
  [QuoteStatus.quoted]: {
    [QuoteFeature.EDIT_DESCRIPTION]: false,
    [QuoteFeature.EDIT_CONTACT]: false,
    [QuoteFeature.CLEAR_CONTACT]: false,
    [QuoteFeature.EDIT_ESTIMATOR]: true,
    [QuoteFeature.EDIT_SALESPERSON]: true,
    [QuoteFeature.SET_OPPORTUNITY]: true,
    [QuoteFeature.CLEAR_OPPORTUNITY]: false,
    [QuoteFeature.EDIT_CAMPAIGN]: true,
    [QuoteFeature.EDIT_JOB_TYPE]: true,
    [QuoteFeature.ADJUST_TOTAL_PRICE]: false,
    [QuoteFeature.EDIT_ORDER_NUMBER]: true,
    [QuoteFeature.EDIT_GROSS_VALUE]: true,
    [QuoteFeature.EDIT_PROBABILITY]: true,
    [QuoteFeature.EDIT_TAX_CODE]: false,
    [QuoteFeature.EDIT_LETTER_HEADER]: false,
    [QuoteFeature.EDIT_LETTER_FOOTER]: false,
    [QuoteFeature.EDIT_SIGNATURE]: true,
    [QuoteFeature.EDIT_TERMS]: true,
    [QuoteFeature.CHANGE_PRINT_OPTIONS]: true,
    [QuoteFeature.EDIT_ORDER_REASON]: false,
    [QuoteFeature.EDIT_REJECT_REASON]: false,
    [QuoteFeature.EDIT_REJECT_NOTES]: false,
    [QuoteFeature.EDIT_EXPECTED_DATE]: true,
    [QuoteFeature.EDIT_QUOTE_REQUIRED_DATE]: true,
    [QuoteFeature.EDIT_PLANNED_START_DATE]: true,
    [QuoteFeature.EDIT_TARGET_DATE]: true,
    [QuoteFeature.EDIT_REQUIRED_DATE]: true,
    [QuoteFeature.EDIT_EXPIRY_DATE]: true,
    [QuoteFeature.EDIT_ADDRESSES]: true,
    [QuoteFeature.EDIT_NOTES]: true,
    [QuoteFeature.EDIT_USER_FIELDS]: true,
    [QuoteFeature.ADD_LINE_ITEMS]: false,
    [QuoteFeature.EDIT_LINE_ITEMS]: false,
    [QuoteFeature.DELETE_LINE_ITEMS]: false,
    [QuoteFeature.DORMANT_LINE_ITEMS]: false,
    [QuoteFeature.REORDER_LINE_ITEMS]: false,
    [QuoteFeature.CHANGE_QUOTE_STATUS]: true,
    [QuoteFeature.CREATE_NEW_REVISION]: true,
    [QuoteFeature.VIEW_AMENDMENTS]: false,
    [QuoteFeature.EDIT_REFERRALS]: true,
  },
  [QuoteStatus.confirmed]: {
    [QuoteFeature.EDIT_DESCRIPTION]: 'amend',
    [QuoteFeature.EDIT_CONTACT]: false,
    [QuoteFeature.CLEAR_CONTACT]: false,
    [QuoteFeature.EDIT_ESTIMATOR]: true,
    [QuoteFeature.EDIT_SALESPERSON]: true,
    [QuoteFeature.SET_OPPORTUNITY]: true,
    [QuoteFeature.CLEAR_OPPORTUNITY]: false,
    [QuoteFeature.EDIT_CAMPAIGN]: true,
    [QuoteFeature.EDIT_JOB_TYPE]: true,
    [QuoteFeature.ADJUST_TOTAL_PRICE]: 'amend',
    [QuoteFeature.EDIT_ORDER_NUMBER]: true,
    [QuoteFeature.EDIT_GROSS_VALUE]: true,
    [QuoteFeature.EDIT_PROBABILITY]: false,
    [QuoteFeature.EDIT_TAX_CODE]: false,
    [QuoteFeature.EDIT_LETTER_HEADER]: false,
    [QuoteFeature.EDIT_LETTER_FOOTER]: false,
    [QuoteFeature.EDIT_SIGNATURE]: true,
    [QuoteFeature.EDIT_TERMS]: true,
    [QuoteFeature.CHANGE_PRINT_OPTIONS]: true,
    [QuoteFeature.EDIT_ORDER_REASON]: true,
    [QuoteFeature.EDIT_REJECT_REASON]: false,
    [QuoteFeature.EDIT_REJECT_NOTES]: false,
    [QuoteFeature.EDIT_EXPECTED_DATE]: true,
    [QuoteFeature.EDIT_QUOTE_REQUIRED_DATE]: true,
    [QuoteFeature.EDIT_PLANNED_START_DATE]: true,
    [QuoteFeature.EDIT_TARGET_DATE]: true,
    [QuoteFeature.EDIT_REQUIRED_DATE]: true,
    [QuoteFeature.EDIT_EXPIRY_DATE]: false,
    [QuoteFeature.EDIT_ADDRESSES]: true,
    [QuoteFeature.EDIT_NOTES]: true,
    [QuoteFeature.EDIT_USER_FIELDS]: true,
    [QuoteFeature.ADD_LINE_ITEMS]: 'amend',
    [QuoteFeature.EDIT_LINE_ITEMS]: 'amend',
    [QuoteFeature.DELETE_LINE_ITEMS]: 'amend',
    [QuoteFeature.DORMANT_LINE_ITEMS]: 'amend',
    [QuoteFeature.REORDER_LINE_ITEMS]: 'amend',
    [QuoteFeature.CHANGE_QUOTE_STATUS]: true,
    [QuoteFeature.CREATE_NEW_REVISION]: false,
    [QuoteFeature.VIEW_AMENDMENTS]: true,
    [QuoteFeature.EDIT_REFERRALS]: true,
  },
  [QuoteStatus.partDelivered]: {
    [QuoteFeature.EDIT_DESCRIPTION]: 'amend',
    [QuoteFeature.EDIT_CONTACT]: false,
    [QuoteFeature.CLEAR_CONTACT]: false,
    [QuoteFeature.EDIT_ESTIMATOR]: true,
    [QuoteFeature.EDIT_SALESPERSON]: true,
    [QuoteFeature.SET_OPPORTUNITY]: true,
    [QuoteFeature.CLEAR_OPPORTUNITY]: false,
    [QuoteFeature.EDIT_CAMPAIGN]: true,
    [QuoteFeature.EDIT_JOB_TYPE]: true,
    [QuoteFeature.ADJUST_TOTAL_PRICE]: 'amend',
    [QuoteFeature.EDIT_ORDER_NUMBER]: true,
    [QuoteFeature.EDIT_GROSS_VALUE]: true,
    [QuoteFeature.EDIT_PROBABILITY]: false,
    [QuoteFeature.EDIT_TAX_CODE]: false,
    [QuoteFeature.EDIT_LETTER_HEADER]: false,
    [QuoteFeature.EDIT_LETTER_FOOTER]: false,
    [QuoteFeature.EDIT_SIGNATURE]: true,
    [QuoteFeature.EDIT_TERMS]: true,
    [QuoteFeature.CHANGE_PRINT_OPTIONS]: true,
    [QuoteFeature.EDIT_ORDER_REASON]: true,
    [QuoteFeature.EDIT_REJECT_REASON]: false,
    [QuoteFeature.EDIT_REJECT_NOTES]: false,
    [QuoteFeature.EDIT_EXPECTED_DATE]: true,
    [QuoteFeature.EDIT_QUOTE_REQUIRED_DATE]: true,
    [QuoteFeature.EDIT_PLANNED_START_DATE]: true,
    [QuoteFeature.EDIT_TARGET_DATE]: true,
    [QuoteFeature.EDIT_REQUIRED_DATE]: true,
    [QuoteFeature.EDIT_EXPIRY_DATE]: false,
    [QuoteFeature.EDIT_ADDRESSES]: true,
    [QuoteFeature.EDIT_NOTES]: true,
    [QuoteFeature.EDIT_USER_FIELDS]: true,
    [QuoteFeature.ADD_LINE_ITEMS]: 'amend',
    [QuoteFeature.EDIT_LINE_ITEMS]: 'amend',
    [QuoteFeature.DELETE_LINE_ITEMS]: 'amend',
    [QuoteFeature.DORMANT_LINE_ITEMS]: 'amend',
    [QuoteFeature.REORDER_LINE_ITEMS]: 'amend',
    [QuoteFeature.CHANGE_QUOTE_STATUS]: true,
    [QuoteFeature.CREATE_NEW_REVISION]: false,
    [QuoteFeature.VIEW_AMENDMENTS]: true,
    [QuoteFeature.EDIT_REFERRALS]: true,
  },
  [QuoteStatus.delivered]: {
    [QuoteFeature.EDIT_DESCRIPTION]: 'amend',
    [QuoteFeature.EDIT_CONTACT]: false,
    [QuoteFeature.CLEAR_CONTACT]: false,
    [QuoteFeature.EDIT_ESTIMATOR]: true,
    [QuoteFeature.EDIT_SALESPERSON]: true,
    [QuoteFeature.SET_OPPORTUNITY]: true,
    [QuoteFeature.CLEAR_OPPORTUNITY]: false,
    [QuoteFeature.EDIT_CAMPAIGN]: true,
    [QuoteFeature.EDIT_JOB_TYPE]: true,
    [QuoteFeature.ADJUST_TOTAL_PRICE]: 'amend',
    [QuoteFeature.EDIT_ORDER_NUMBER]: true,
    [QuoteFeature.EDIT_GROSS_VALUE]: true,
    [QuoteFeature.EDIT_PROBABILITY]: false,
    [QuoteFeature.EDIT_TAX_CODE]: false,
    [QuoteFeature.EDIT_LETTER_HEADER]: false,
    [QuoteFeature.EDIT_LETTER_FOOTER]: false,
    [QuoteFeature.EDIT_SIGNATURE]: true,
    [QuoteFeature.EDIT_TERMS]: true,
    [QuoteFeature.CHANGE_PRINT_OPTIONS]: true,
    [QuoteFeature.EDIT_ORDER_REASON]: true,
    [QuoteFeature.EDIT_REJECT_REASON]: false,
    [QuoteFeature.EDIT_REJECT_NOTES]: false,
    [QuoteFeature.EDIT_EXPECTED_DATE]: true,
    [QuoteFeature.EDIT_QUOTE_REQUIRED_DATE]: true,
    [QuoteFeature.EDIT_PLANNED_START_DATE]: true,
    [QuoteFeature.EDIT_TARGET_DATE]: true,
    [QuoteFeature.EDIT_REQUIRED_DATE]: true,
    [QuoteFeature.EDIT_EXPIRY_DATE]: false,
    [QuoteFeature.EDIT_ADDRESSES]: true,
    [QuoteFeature.EDIT_NOTES]: true,
    [QuoteFeature.EDIT_USER_FIELDS]: true,
    [QuoteFeature.ADD_LINE_ITEMS]: 'amend',
    [QuoteFeature.EDIT_LINE_ITEMS]: 'amend',
    [QuoteFeature.DELETE_LINE_ITEMS]: 'amend',
    [QuoteFeature.DORMANT_LINE_ITEMS]: 'amend',
    [QuoteFeature.REORDER_LINE_ITEMS]: 'amend',
    [QuoteFeature.CHANGE_QUOTE_STATUS]: true,
    [QuoteFeature.CREATE_NEW_REVISION]: false,
    [QuoteFeature.VIEW_AMENDMENTS]: true,
    [QuoteFeature.EDIT_REFERRALS]: true,
  },
  [QuoteStatus.invoiced]: {
    [QuoteFeature.EDIT_DESCRIPTION]: 'amend',
    [QuoteFeature.EDIT_CONTACT]: false,
    [QuoteFeature.CLEAR_CONTACT]: false,
    [QuoteFeature.EDIT_ESTIMATOR]: true,
    [QuoteFeature.EDIT_SALESPERSON]: true,
    [QuoteFeature.SET_OPPORTUNITY]: false,
    [QuoteFeature.CLEAR_OPPORTUNITY]: false,
    [QuoteFeature.EDIT_CAMPAIGN]: true,
    [QuoteFeature.EDIT_JOB_TYPE]: true,
    [QuoteFeature.ADJUST_TOTAL_PRICE]: 'amend',
    [QuoteFeature.EDIT_ORDER_NUMBER]: true,
    [QuoteFeature.EDIT_GROSS_VALUE]: true,
    [QuoteFeature.EDIT_PROBABILITY]: false,
    [QuoteFeature.EDIT_TAX_CODE]: false,
    [QuoteFeature.EDIT_LETTER_HEADER]: false,
    [QuoteFeature.EDIT_LETTER_FOOTER]: false,
    [QuoteFeature.EDIT_SIGNATURE]: false,
    [QuoteFeature.EDIT_TERMS]: false,
    [QuoteFeature.CHANGE_PRINT_OPTIONS]: true,
    [QuoteFeature.EDIT_ORDER_REASON]: true,
    [QuoteFeature.EDIT_REJECT_REASON]: false,
    [QuoteFeature.EDIT_REJECT_NOTES]: false,
    [QuoteFeature.EDIT_EXPECTED_DATE]: false,
    [QuoteFeature.EDIT_QUOTE_REQUIRED_DATE]: false,
    [QuoteFeature.EDIT_PLANNED_START_DATE]: false,
    [QuoteFeature.EDIT_TARGET_DATE]: false,
    [QuoteFeature.EDIT_REQUIRED_DATE]: false,
    [QuoteFeature.EDIT_EXPIRY_DATE]: false,
    [QuoteFeature.EDIT_ADDRESSES]: true,
    [QuoteFeature.EDIT_NOTES]: true,
    [QuoteFeature.EDIT_USER_FIELDS]: true,
    [QuoteFeature.ADD_LINE_ITEMS]: 'amend',
    [QuoteFeature.EDIT_LINE_ITEMS]: 'amend',
    [QuoteFeature.DELETE_LINE_ITEMS]: 'amend',
    [QuoteFeature.DORMANT_LINE_ITEMS]: 'amend',
    [QuoteFeature.REORDER_LINE_ITEMS]: 'amend',
    [QuoteFeature.CHANGE_QUOTE_STATUS]: true,
    [QuoteFeature.CREATE_NEW_REVISION]: false,
    [QuoteFeature.VIEW_AMENDMENTS]: true,
    [QuoteFeature.EDIT_REFERRALS]: false,
  },
  [QuoteStatus.credited]: {
    [QuoteFeature.EDIT_DESCRIPTION]: false,
    [QuoteFeature.EDIT_CONTACT]: false,
    [QuoteFeature.CLEAR_CONTACT]: false,
    [QuoteFeature.EDIT_ESTIMATOR]: true,
    [QuoteFeature.EDIT_SALESPERSON]: true,
    [QuoteFeature.SET_OPPORTUNITY]: false,
    [QuoteFeature.CLEAR_OPPORTUNITY]: false,
    [QuoteFeature.EDIT_CAMPAIGN]: true,
    [QuoteFeature.EDIT_JOB_TYPE]: true,
    [QuoteFeature.ADJUST_TOTAL_PRICE]: false,
    [QuoteFeature.EDIT_ORDER_NUMBER]: true,
    [QuoteFeature.EDIT_GROSS_VALUE]: true,
    [QuoteFeature.EDIT_PROBABILITY]: false,
    [QuoteFeature.EDIT_TAX_CODE]: false,
    [QuoteFeature.EDIT_LETTER_HEADER]: false,
    [QuoteFeature.EDIT_LETTER_FOOTER]: false,
    [QuoteFeature.EDIT_SIGNATURE]: false,
    [QuoteFeature.EDIT_TERMS]: false,
    [QuoteFeature.CHANGE_PRINT_OPTIONS]: true,
    [QuoteFeature.EDIT_ORDER_REASON]: true,
    [QuoteFeature.EDIT_REJECT_REASON]: false,
    [QuoteFeature.EDIT_REJECT_NOTES]: false,
    [QuoteFeature.EDIT_EXPECTED_DATE]: false,
    [QuoteFeature.EDIT_QUOTE_REQUIRED_DATE]: false,
    [QuoteFeature.EDIT_PLANNED_START_DATE]: false,
    [QuoteFeature.EDIT_TARGET_DATE]: false,
    [QuoteFeature.EDIT_REQUIRED_DATE]: false,
    [QuoteFeature.EDIT_EXPIRY_DATE]: false,
    [QuoteFeature.EDIT_ADDRESSES]: true,
    [QuoteFeature.EDIT_NOTES]: true,
    [QuoteFeature.EDIT_USER_FIELDS]: true,
    [QuoteFeature.ADD_LINE_ITEMS]: false,
    [QuoteFeature.EDIT_LINE_ITEMS]: false,
    [QuoteFeature.DELETE_LINE_ITEMS]: false,
    [QuoteFeature.DORMANT_LINE_ITEMS]: false,
    [QuoteFeature.REORDER_LINE_ITEMS]: false,
    [QuoteFeature.CHANGE_QUOTE_STATUS]: true,
    [QuoteFeature.CREATE_NEW_REVISION]: false,
    [QuoteFeature.VIEW_AMENDMENTS]: true,
    [QuoteFeature.EDIT_REFERRALS]: false,
  },
  [QuoteStatus.paid]: {
    [QuoteFeature.EDIT_DESCRIPTION]: false,
    [QuoteFeature.EDIT_CONTACT]: false,
    [QuoteFeature.CLEAR_CONTACT]: false,
    [QuoteFeature.EDIT_ESTIMATOR]: true,
    [QuoteFeature.EDIT_SALESPERSON]: true,
    [QuoteFeature.SET_OPPORTUNITY]: false,
    [QuoteFeature.CLEAR_OPPORTUNITY]: false,
    [QuoteFeature.EDIT_CAMPAIGN]: true,
    [QuoteFeature.EDIT_JOB_TYPE]: true,
    [QuoteFeature.ADJUST_TOTAL_PRICE]: false,
    [QuoteFeature.EDIT_ORDER_NUMBER]: true,
    [QuoteFeature.EDIT_GROSS_VALUE]: true,
    [QuoteFeature.EDIT_PROBABILITY]: false,
    [QuoteFeature.EDIT_TAX_CODE]: false,
    [QuoteFeature.EDIT_LETTER_HEADER]: false,
    [QuoteFeature.EDIT_LETTER_FOOTER]: false,
    [QuoteFeature.EDIT_SIGNATURE]: false,
    [QuoteFeature.EDIT_TERMS]: false,
    [QuoteFeature.CHANGE_PRINT_OPTIONS]: true,
    [QuoteFeature.EDIT_ORDER_REASON]: true,
    [QuoteFeature.EDIT_REJECT_REASON]: false,
    [QuoteFeature.EDIT_REJECT_NOTES]: false,
    [QuoteFeature.EDIT_EXPECTED_DATE]: false,
    [QuoteFeature.EDIT_QUOTE_REQUIRED_DATE]: false,
    [QuoteFeature.EDIT_PLANNED_START_DATE]: false,
    [QuoteFeature.EDIT_TARGET_DATE]: false,
    [QuoteFeature.EDIT_REQUIRED_DATE]: false,
    [QuoteFeature.EDIT_EXPIRY_DATE]: false,
    [QuoteFeature.EDIT_ADDRESSES]: true,
    [QuoteFeature.EDIT_NOTES]: true,
    [QuoteFeature.EDIT_USER_FIELDS]: true,
    [QuoteFeature.ADD_LINE_ITEMS]: false,
    [QuoteFeature.EDIT_LINE_ITEMS]: false,
    [QuoteFeature.DELETE_LINE_ITEMS]: false,
    [QuoteFeature.DORMANT_LINE_ITEMS]: false,
    [QuoteFeature.REORDER_LINE_ITEMS]: false,
    [QuoteFeature.CHANGE_QUOTE_STATUS]: true,
    [QuoteFeature.CREATE_NEW_REVISION]: false,
    [QuoteFeature.VIEW_AMENDMENTS]: true,
    [QuoteFeature.EDIT_REFERRALS]: false,
  },
  [QuoteStatus.closed]: {
    [QuoteFeature.EDIT_DESCRIPTION]: false,
    [QuoteFeature.EDIT_CONTACT]: false,
    [QuoteFeature.CLEAR_CONTACT]: false,
    [QuoteFeature.EDIT_ESTIMATOR]: true,
    [QuoteFeature.EDIT_SALESPERSON]: true,
    [QuoteFeature.SET_OPPORTUNITY]: false,
    [QuoteFeature.CLEAR_OPPORTUNITY]: false,
    [QuoteFeature.EDIT_CAMPAIGN]: true,
    [QuoteFeature.EDIT_JOB_TYPE]: true,
    [QuoteFeature.ADJUST_TOTAL_PRICE]: false,
    [QuoteFeature.EDIT_ORDER_NUMBER]: false,
    [QuoteFeature.EDIT_GROSS_VALUE]: true,
    [QuoteFeature.EDIT_PROBABILITY]: false,
    [QuoteFeature.EDIT_TAX_CODE]: false,
    [QuoteFeature.EDIT_LETTER_HEADER]: false,
    [QuoteFeature.EDIT_LETTER_FOOTER]: false,
    [QuoteFeature.EDIT_SIGNATURE]: false,
    [QuoteFeature.EDIT_TERMS]: false,
    [QuoteFeature.CHANGE_PRINT_OPTIONS]: false,
    [QuoteFeature.EDIT_ORDER_REASON]: false,
    [QuoteFeature.EDIT_REJECT_REASON]: false,
    [QuoteFeature.EDIT_REJECT_NOTES]: false,
    [QuoteFeature.EDIT_EXPECTED_DATE]: false,
    [QuoteFeature.EDIT_QUOTE_REQUIRED_DATE]: false,
    [QuoteFeature.EDIT_PLANNED_START_DATE]: false,
    [QuoteFeature.EDIT_TARGET_DATE]: false,
    [QuoteFeature.EDIT_REQUIRED_DATE]: false,
    [QuoteFeature.EDIT_EXPIRY_DATE]: false,
    [QuoteFeature.EDIT_ADDRESSES]: true,
    [QuoteFeature.EDIT_NOTES]: true,
    [QuoteFeature.EDIT_USER_FIELDS]: true,
    [QuoteFeature.ADD_LINE_ITEMS]: false,
    [QuoteFeature.EDIT_LINE_ITEMS]: false,
    [QuoteFeature.DELETE_LINE_ITEMS]: false,
    [QuoteFeature.DORMANT_LINE_ITEMS]: false,
    [QuoteFeature.REORDER_LINE_ITEMS]: false,
    [QuoteFeature.CHANGE_QUOTE_STATUS]: true,
    [QuoteFeature.CREATE_NEW_REVISION]: false,
    [QuoteFeature.VIEW_AMENDMENTS]: false,
    [QuoteFeature.EDIT_REFERRALS]: false,
  },
  [QuoteStatus.rejected]: {
    [QuoteFeature.EDIT_DESCRIPTION]: false,
    [QuoteFeature.EDIT_CONTACT]: false,
    [QuoteFeature.CLEAR_CONTACT]: false,
    [QuoteFeature.EDIT_ESTIMATOR]: true,
    [QuoteFeature.EDIT_SALESPERSON]: true,
    [QuoteFeature.SET_OPPORTUNITY]: false,
    [QuoteFeature.CLEAR_OPPORTUNITY]: false,
    [QuoteFeature.EDIT_CAMPAIGN]: true,
    [QuoteFeature.EDIT_JOB_TYPE]: true,
    [QuoteFeature.ADJUST_TOTAL_PRICE]: false,
    [QuoteFeature.EDIT_ORDER_NUMBER]: false,
    [QuoteFeature.EDIT_GROSS_VALUE]: true,
    [QuoteFeature.EDIT_PROBABILITY]: false,
    [QuoteFeature.EDIT_TAX_CODE]: false,
    [QuoteFeature.EDIT_LETTER_HEADER]: false,
    [QuoteFeature.EDIT_LETTER_FOOTER]: false,
    [QuoteFeature.EDIT_SIGNATURE]: false,
    [QuoteFeature.EDIT_TERMS]: false,
    [QuoteFeature.CHANGE_PRINT_OPTIONS]: false,
    [QuoteFeature.EDIT_ORDER_REASON]: false,
    [QuoteFeature.EDIT_REJECT_REASON]: true,
    [QuoteFeature.EDIT_REJECT_NOTES]: true,
    [QuoteFeature.EDIT_EXPECTED_DATE]: false,
    [QuoteFeature.EDIT_QUOTE_REQUIRED_DATE]: false,
    [QuoteFeature.EDIT_PLANNED_START_DATE]: false,
    [QuoteFeature.EDIT_TARGET_DATE]: false,
    [QuoteFeature.EDIT_REQUIRED_DATE]: false,
    [QuoteFeature.EDIT_EXPIRY_DATE]: false,
    [QuoteFeature.EDIT_ADDRESSES]: true,
    [QuoteFeature.EDIT_NOTES]: true,
    [QuoteFeature.EDIT_USER_FIELDS]: true,
    [QuoteFeature.ADD_LINE_ITEMS]: false,
    [QuoteFeature.EDIT_LINE_ITEMS]: false,
    [QuoteFeature.DELETE_LINE_ITEMS]: false,
    [QuoteFeature.DORMANT_LINE_ITEMS]: false,
    [QuoteFeature.REORDER_LINE_ITEMS]: false,
    [QuoteFeature.CHANGE_QUOTE_STATUS]: true,
    [QuoteFeature.CREATE_NEW_REVISION]: false,
    [QuoteFeature.VIEW_AMENDMENTS]: false,
    [QuoteFeature.EDIT_REFERRALS]: false,
  },
}

// Used in selected quote reducer and sagas for undo...
export {quoteChangeActions}

export const isFeatureEnabled = (feature, order, user, amendMode) => {
  const status = QuoteStatusFeatures[order.status.value]
  if (isEmpty(status)) return false
  const value = status[feature]
  if (value == null) return false
  if (value === 'amend') return amendMode
  return value
}

export const isProductionNotesEnabled = (isEditable, productionNotesTemplatesOptions) => {
  return isEditable && !isEmpty(productionNotesTemplatesOptions)
}

export const isContactMandatory = (order) => {
  const orderStatus = get(order, 'status.value', null)
  const statuses = QuoteStatusFeatures[orderStatus]
  if (isEmpty(statuses)) return true
  return !statuses[QuoteFeature.CLEAR_CONTACT]
}

export const getQuotingFilterParams = (
  allRevisions,
  search,
  statusTypes,
  companies,
  userTypes,
  contacts,
  dateTypes,
  includeFullyScheduled,
  targetStart,
  targetEnd,
  orderTypeId,
  userSettings,
  fixedStatusOptions,
  includeFullyDelivered = true) => {

  const params = {}
  if (getSearchTerm(search))
    params['search'] = search

  params['allRevisions'] = allRevisions
  params['includeFullyScheduled'] = includeFullyScheduled

  if (!isEmpty(statusTypes)) {
    params['status'] = statusTypes
  } else if (!isEmpty(fixedStatusOptions)) {
    params['status'] = includeFullyDelivered
      ? [...fixedStatusOptions, QuoteStatus.delivered]
      : fixedStatusOptions
  }

  if (companies)
    params['companyId'] = companies.map(company => company.value)

  const deliveryStatuses = [DeliveryStatus.none, DeliveryStatus.partDelivered]
  if (includeFullyDelivered) deliveryStatuses.push(DeliveryStatus.fullyDelivered)

  params['deliveryStatus'] = deliveryStatuses

  getQuotePersonParams(params, contacts, userTypes)

  if (targetStart && targetEnd) {
    dateTypes.forEach(dateType => {
      params[`${dateType}Start`] = targetStart
      params[`${dateType}End`] = targetEnd
    })
  }


  updateParamsForOrderType(params, orderTypeId)

  const excludeHomeCompany = get(userSettings, 'excludeHomeCompanyFromReporting', false)

  params['showCompleted'] = true
  params['showInactive'] = true
  params['total'] = true
  params['totalCostSum'] = true
  params['totalMargin'] = true
  params['excludeHomeCompany'] = excludeHomeCompany
  return params
}

export const updateTaxCodeOnOrder = (order, taxCode) => {
  return {
    ...order,
    taxCodeId: taxCode.id,
    taxRate: taxCode.rate,
    taxCode,
  }
}

export const updateTaxCodeOnOrderItems = (orderItems, taxCode, currencies) => {
  return orderItems.map(orderItem => getCalculatedOrderItem({
    ...orderItem,
    taxCodeId: taxCode.id,
    taxRate: taxCode.rate,
    taxCode,
  }, currencies, 'taxCodeId'))
}

export const anyOrderItemHasMsrp = (orderItems) => orderItems && orderItems.some(orderItemHasMsrp)

export const orderItemHasMsrp = (orderItem) => {
  const hasMsrpValue = !isNil(orderItem.msrp)
  const hasPartMsrpPricing = orderItem.part ? orderItem.part.msrpPricing : false
  return (hasMsrpValue && orderItem.msrp > 0) || hasPartMsrpPricing
}

export const getOrderItemMsrp = (orderItem) => {
  const partPackId = get(orderItem, 'partPackId', null)
  const partPacks = get(orderItem, 'part.packs', [])
  const partPack = getPartPackFor(partPackId, partPacks)
  return isNil(partPack) ? 0 : get(partPack, 'msrp', 0)
}

export const getTotalOrderItemValues = (orderItems, currencies, isSubItems = false) => {
  const totalCost = orderItems.reduce((total, item) => total + item.totalCost, 0)
  const totalCostInHomeCurrency = orderItems.reduce((total, item) =>
    total + (item.totalCost * item.currencyRate), 0)
  const totalPrice = orderItems.reduce((total, item) => total + item.totalPrice, 0)
  const totalNetPrice = isSubItems ? totalPrice : orderItems.reduce((total, item) => total + item.netPrice, 0)

  const totalProfit = orderItems.reduce((total, item) =>
    total + getProfit(isSubItems ? item.totalPrice : item.netPrice, item.totalCost, item.currencyRate), 0)

  const totalCostInHomeCurrencyForMsrpItems = orderItems.reduce((total, item) => {
    const part = get(item, 'part', null)
    const pricingRate = getPartSaleRate(part, currencies)
    return total + (orderItemHasMsrp(item) ? convertToCurrency(item.totalCost, pricingRate) : 0)
  }, 0)

  const totalMsrp = orderItems.reduce((total, item) => {
    return total + (orderItemHasMsrp(item) ? getOrderItemMsrp(item) * item.quantity : 0)
  }, 0)

  const totalReferralValue = isSubItems ? 0 : orderItems.reduce((total, item) => {
    const referralFeePc = getOrderItemReferralFee(item)
    return total + getReferralValue(item.netPrice, referralFeePc)
  }, 0)

  return {
    overallSupplierDiscount:
      getSupplierDiscountFromCostInHomeCurrency(totalCostInHomeCurrencyForMsrpItems, totalMsrp),
    overallMargin: getMargin(totalPrice, totalCostInHomeCurrency),
    overallMarkup: getMarkup(totalPrice, totalCostInHomeCurrency),
    overallNetMargin: getMargin(totalNetPrice, totalCostInHomeCurrency),
    overallNetMarkup: getMarkup(totalNetPrice, totalCostInHomeCurrency),
    overallDiscount: isSubItems
      ? null
      : getDiscountWithReferralValue(totalPrice, totalNetPrice, totalReferralValue),
    totalMsrp,
    totalCost,
    totalPrice,
    totalNetPrice,
    totalReferralValue,
    totalProfit,
  }
}

export const getDeliveryAddress = (order) => get(order, 'overrides.deliveryAddress')

export const isDeliveryAddressPopulated = (order) => {
  return !isEmpty(getAddress(getDeliveryAddress(order)))
}

export const getMandatoryTagIds = (settings) => {
  const str = get(settings, 'orderConfirmMandatoryTags', '')
  if (!str) return new Set()
  return new Set(str.split(',').map(Number).filter(n => !isNaN(n)))
}

export const toggleMandatoryTag = (settings, tagId) => {
  const ids = getMandatoryTagIds(settings)
  if (ids.has(tagId)) ids.delete(tagId)
  else ids.add(tagId)
  return Array.from(ids).join(',')
}

export const isTagMandatory = (settings, tagId) => {
  return getMandatoryTagIds(settings).has(tagId)
}

export const getMandatoryQuotationFields = (quotationFields, settings) => {
  if (!quotationFields || !settings) return []
  const mandatoryIds = getMandatoryTagIds(settings)
  const filtered = quotationFields
    .map((field, i) => ({...field, _index: i}))
    .filter(field => mandatoryIds.has(field.id))
  return filtered.sort((a, b) => {
    const catA = a.categoryName || ''
    const catB = b.categoryName || ''
    if (catA !== catB) return catA.localeCompare(catB)
    const orderA = !isNil(a.order) ? a.order : a._index
    const orderB = !isNil(b.order) ? b.order : b._index
    return orderA - orderB
  })
}

export const getOrderTypeOptions = (orderTypes) => {
  return orderTypes.map(orderType => {
    return {
      id: orderType.id,
      key: orderType.id,
      value: orderType.id,
      label: orderType.name,
    }
  })
}

export const getOrderItemLetter = (orderItemIndex) => {
  return getNumberFromLetterSequence(orderItemIndex)
}

export const getNetMarkupWhenDiscountIsApplied = (orderItem) => {
  const markup = get(orderItem, 'markup', 0)
  const netPrice = get(orderItem, 'netPrice', 0)
  const totalCost = get(orderItem, 'totalCost', 0)
  const currencyRate = get(orderItem, 'currencyRate', 0)
  const netMarkup = getMarkup(netPrice, totalCost, currencyRate)
  const discount = get(orderItem, 'discount', 0)
  return !isNil(discount) && discount > 0
    ? getPercentageString(netMarkup, 2)
    : getPercentageString(markup, 2)
}

export const getNetMarginWhenDiscountIsApplied = (orderItem) => {
  const margin = get(orderItem, 'margin', 0)
  const netPrice = get(orderItem, 'netPrice', 0)
  const totalCost = get(orderItem, 'totalCost', 0)
  const currencyRate = get(orderItem, 'currencyRate', 0)
  const netMargin = getMargin(netPrice, totalCost, currencyRate)
  const discount = get(orderItem, 'discount', 0)
  return !isNil(discount) && discount > 0
    ? getPercentageString(netMargin, 2)
    : getPercentageString(margin, 2)
}

export const getOrderCompany = (order) => {
  const company = get(order, 'company', null)
  return (!isNil(order)) ? company : {name: ''}
}

export const getOrderCompanyName = (order) => {
  const company = getOrderCompany(order)
  const companyName = get(company, 'name', null)
  return (!isNil(company)) ? companyName : ''
}

export const getOrderContact = (order) => {
  const contact = get(order, 'contact', null)
  return (!isNil(order)) ? contact : {forename: '', surname: ''}
}

export const getOrderContactName = (order) => {
  const contact = getOrderContact(order)
  const forename = get(contact, 'forename', null)
  const surname = get(contact, 'surname', null)
  return (!isNil(contact)) ? forename + ' ' + surname : ''
}

export const getOrderLocation = (order) => {
  const contact = getOrderContact(order)
  const defaultLocation = {
    address1: '',
    address2: '',
    address3: '',
    city: '',
    county: '',
    postCode: '',
    country: '',
    phone: get(contact, 'phone', ''),
    mobile: get(contact, 'mobile', ''),
  }
  const location = get(contact, 'location', defaultLocation)

  return {
    company: getOrderCompanyName(order),
    contact: getOrderContactName(order),
    address1: location.address1,
    address2: location.address2,
    address3: location.address3,
    city: location.city,
    county: location.county,
    postCode: location.postCode,
    country: location.country,
    countryId: location.country,
    phone: location.phone,
    mobile: get(contact, 'mobile', ''),
  }
}

export const getTotalInvoicedInHomeCurrency = (order) => {
  return order.totalInvoiced / order.currencyRate
}

export const getRemainingToBeInvoiced = (order) => {
  return Math.max(0, order.reportedPrice - order.totalInvoiced)
}

export const getRemainingToBeInvoicedInHomeCurrency = (order) => {
  return getRemainingToBeInvoiced(order) / order.currencyRate
}

export default {
  QuoteStatus,
  QuoteFeature,
  OrderReasonType,
  TextTemplateTypes,
  ReferralType,
  getReferralTypeName,
  CoverReferralFee,
  getCoverReferralFeeName,
  findQuoteStatus,
  getStatusForSelectedQuoteList,
  getQuoteTypes,
  getParamsForType,
  getQuoteStatus,
  getOpportunityName,
  getAccountManagerName,
  getNameFromObjectForKey,
  getColorForProductionStatus,
  getColorForQuoteStatus,
  filterDeletedOrderItems,
  getOrderItemLetter,
  getOrderItemReferralFee,
  getCalculatedOrderItem,
  getCalculatedTotals,
  getNominalCode,
  getTextTemplateOptionsByType,
  getValidatedSendAddresses,
  getCurrency,
  canEditQuote,
  supportsAmend,
  getEditQuoteReason,
  isFeatureEnabled,
  isProductionNotesEnabled,
  JobStatuses,
  QuoteDateTypes,
  isContactMandatory,
  getDeliveryAddress,
  isDeliveryAddressPopulated,
}
